/**
 * Main-thread facade for the Maia 3 worker.
 *
 * Owns the worker lifecycle, dispatches inference requests, and rejects any
 * pending work on `dispose()` (so React unmount mid-game doesn't hang a Promise).
 */

import { preprocessFen, decodeMove } from './tensor';
import { MOVE_INDEX, mirrorMove } from './moves';

export type MaiaStatus =
  | 'loading'       // checking IndexedDB
  | 'no-cache'      // need to call downloadModel()
  | 'downloading'
  | 'ready'
  | 'error';

/** Output of Maia3.analyze() — probabilities from the side-to-move's perspective. */
export interface MaiaAnalysis {
  /** P(side-to-move wins). */
  winProb: number;
  /** P(draw). */
  drawProb: number;
  /** P(side-to-move loses). */
  lossProb: number;
  /** Top-K most likely moves at the given rating, UCI strings in original-FEN coordinates. */
  topMoves: Array<{ uci: string; prob: number }>;
  /** Probability of a specific move (when `playedUci` was passed to analyze). */
  playedMoveProb?: number;
  /** Probabilities for arbitrary UCI moves (when `lookupUcis` was passed). Keyed by UCI. */
  lookupProbs?: Record<string, number>;
}

/**
 * Flip (win, draw, loss) to the user's perspective when the side to move
 * isn't the user. Maia outputs probabilities from the mover's perspective —
 * if the opponent is to move, their win chance is our loss chance.
 */
export function orientToPlayer(
  analysis: Pick<MaiaAnalysis, "winProb" | "drawProb" | "lossProb">,
  fen: string,
  playerColor: "white" | "black",
): { winProb: number; drawProb: number; lossProb: number } {
  const sideToMove = fen.split(" ")[1] === "w" ? "white" : "black";
  if (sideToMove === playerColor) {
    return { winProb: analysis.winProb, drawProb: analysis.drawProb, lossProb: analysis.lossProb };
  }
  return { winProb: analysis.lossProb, drawProb: analysis.drawProb, lossProb: analysis.winProb };
}

interface PendingInference {
  resolve: (value: { logitsMove: Float32Array; logitsValue: Float32Array }) => void;
  reject: (err: Error) => void;
}

export interface Maia3Options {
  /** Absolute URL to the ONNX weights (S3/CloudFront in prod, /maia3/... in dev). */
  modelUrl: string;
  /** Version tag — mismatches evict the cached blob. */
  modelVersion: string;
  /** ONNX WASM thread budget for this instance's session. Omit/0 = ORT default (~all cores). Pool
   *  workers pass a slice (cores/poolSize) so concurrent inferences don't oversubscribe the CPU. */
  numThreads?: number;
  onStatus?: (status: MaiaStatus) => void;
  onProgress?: (progress: number) => void;
  onError?: (message: string) => void;
}

export class Maia3 {
  private worker: Worker | null = null;
  private status: MaiaStatus = 'loading';
  private disposed = false;
  private pending = new Map<number, PendingInference>();
  private nextId = 0;
  private readyPromise: Promise<void> | null = null;
  private readyResolve: (() => void) | null = null;
  private readyReject: ((err: Error) => void) | null = null;
  private downloadPromise: Promise<void> | null = null;
  private downloadResolve: (() => void) | null = null;
  private downloadReject: ((err: Error) => void) | null = null;
  /** Resolves when the worker's `init` has reported its IndexedDB verdict — 'ready' (cache hit, session
   *  already built) or 'no-cache' (a download is genuinely needed). `preload()` MUST await this before
   *  asking for a download; see its docstring. */
  private cacheVerdict: Promise<MaiaStatus> | null = null;
  private cacheVerdictResolve: ((s: MaiaStatus) => void) | null = null;

  constructor(private options: Maia3Options) {
    if (typeof Worker === 'undefined') {
      // SSR / tests — worker isn't available. Caller should guard.
      return;
    }
    this.worker = new Worker('/workers/maia-worker.js');
    this.worker.addEventListener('message', this.handleMessage);
    this.worker.addEventListener('error', (e) => {
      this.setStatus('error');
      this.options.onError?.(e.message || 'worker error');
      // Unblock a preload() waiting on the cache verdict (see preload()) before rejecting the rest.
      this.cacheVerdictResolve?.('error');
      this.cacheVerdictResolve = null;
      this.rejectAll(new Error(e.message || 'worker error'));
    });

    this.readyPromise = new Promise((resolve, reject) => {
      this.readyResolve = resolve;
      this.readyReject = reject;
    });
    // Mark the promise as "handled" so that dispose()-driven rejection before
    // any getMove() caller awaits doesn't trip the global unhandled-rejection
    // handler. Callers that `await engine.ready()` still see the rejection.
    this.readyPromise.catch(() => {});

    this.cacheVerdict = new Promise((resolve) => {
      this.cacheVerdictResolve = resolve;
    });

    this.worker.postMessage({
      type: 'init',
      modelUrl: options.modelUrl,
      modelVersion: options.modelVersion,
      numThreads: options.numThreads,
    });
  }

  /** Trigger the 43MB download. Resolves when the model is loaded + cached. */
  async downloadModel(): Promise<void> {
    if (!this.worker) throw new Error('worker unavailable');
    if (this.status === 'ready') return;
    if (this.downloadPromise) return this.downloadPromise;
    this.downloadPromise = new Promise((resolve, reject) => {
      this.downloadResolve = resolve;
      this.downloadReject = reject;
    });
    this.worker.postMessage({ type: 'download' });
    return this.downloadPromise;
  }

  /** Resolves once the model session is ready (from cache or post-download). */
  async ready(): Promise<void> {
    if (this.status === 'ready') return;
    if (!this.readyPromise) throw new Error('worker unavailable');
    return this.readyPromise;
  }

  /**
   * Eagerly warm the model (load from cache, or download then load).
   *
   * WAITS for the worker's IndexedDB verdict before requesting a download. It used to call
   * `downloadModel()` immediately, whose only early-out is `status === 'ready'` — but status is
   * 'loading' at construction and `init`'s cache check resolves asynchronously, so preload always won
   * the race and forced a fetch even when the model was already cached. With 6 Maia workers on the
   * Review page (1 single + 5 pool) that was ~258MB of redundant transfer per load, warm cache included
   * (measured 2026-08-07). The worker's `download` handler now re-checks the cache too, so this is
   * belt-and-braces: fix the request, and make the handler idempotent.
   */
  async preload(): Promise<void> {
    if (this.status !== 'ready' && this.cacheVerdict) {
      const verdict = await this.cacheVerdict;
      // Cache hit → the worker already built the session from IndexedDB; nothing to download.
      if (verdict !== 'ready') await this.downloadModel();
    } else {
      await this.downloadModel();
    }
    await this.ready();
  }

  /**
   * Pick Maia's move for the given FEN at the requested rating.
   * `eloOppo` is the opponent's rating — affects the model's predictions.
   *
   * Sampling: softmax over the legal-move logits, then weighted random pick.
   * Maia's policy head is a probability distribution over what a human of
   * the target Elo would play — argmax throws away that distribution.
   * If the model says "64% Nf3, 25% Nc3, 11% d4," an opponent that always
   * plays Nf3 feels robotic and deterministic across sessions. Sampling
   * preserves the real variety humans show at that rating.
   */
  async getMove(fen: string, eloSelf: number, eloOppo: number): Promise<string> {
    await this.ready();
    if (!this.worker) throw new Error('worker unavailable');

    const { tokens, legalMoves, wasMirrored } = preprocessFen(fen);
    const { logitsMove } = await this.runInference(tokens, eloSelf, eloOppo);

    // Collect legal-move logits and find the max (for numerically stable softmax).
    const legalIdxs: number[] = [];
    let maxLogit = -Infinity;
    for (let i = 0; i < 4352; i++) {
      if (legalMoves[i] !== 1) continue;
      legalIdxs.push(i);
      if (logitsMove[i] > maxLogit) maxLogit = logitsMove[i];
    }
    if (legalIdxs.length === 0) throw new Error('no legal moves');

    // Softmax with temperature=1 over the legal-move subset.
    // Subtracting maxLogit before exp keeps things in range; doesn't change
    // the resulting probabilities.
    const expVals: number[] = new Array(legalIdxs.length);
    let sumExp = 0;
    for (let k = 0; k < legalIdxs.length; k++) {
      const e = Math.exp(logitsMove[legalIdxs[k]] - maxLogit);
      expVals[k] = e;
      sumExp += e;
    }

    // Weighted random pick.
    let r = Math.random() * sumExp;
    let pickedIdx = legalIdxs[legalIdxs.length - 1]; // safety fallback for float drift
    for (let k = 0; k < legalIdxs.length; k++) {
      r -= expVals[k];
      if (r <= 0) {
        pickedIdx = legalIdxs[k];
        break;
      }
    }
    return decodeMove(pickedIdx, wasMirrored);
  }

  /**
   * Full analysis of the position at the requested rating: win/draw/loss
   * probabilities from the side-to-move's perspective, plus the top-K
   * policy moves (UCI strings, re-mirrored if needed). Callers that want
   * a user-perspective win % must flip the three values when the side to
   * move isn't the user's color.
   */
  async analyze(
    fen: string,
    eloSelf: number,
    eloOppo: number,
    topK: number = 3,
    playedUci?: string,
    lookupUcis?: string[],
  ): Promise<MaiaAnalysis> {
    await this.ready();
    if (!this.worker) throw new Error('worker unavailable');

    const { tokens, legalMoves, wasMirrored } = preprocessFen(fen);
    const { logitsMove, logitsValue } = await this.runInference(tokens, eloSelf, eloOppo);

    // Softmax the value head. Maia 3's ONNX export emits the 3 classes in
    // {loss, draw, win} order from the side-to-move's perspective (observed
    // inversion: card showed W 46% at start + graph spiked UP on a bad
    // white move, consistent with logits[0] being the side-to-move's LOSS
    // prob — i.e. what we were calling winProb was actually lossProb).
    const maxV = Math.max(logitsValue[0], logitsValue[1], logitsValue[2]);
    const expV = [
      Math.exp(logitsValue[0] - maxV),
      Math.exp(logitsValue[1] - maxV),
      Math.exp(logitsValue[2] - maxV),
    ];
    const sumV = expV[0] + expV[1] + expV[2];
    const lossProb = expV[0] / sumV;
    const drawProb = expV[1] / sumV;
    const winProb = expV[2] / sumV;

    // Softmax over legal-only move logits, then take top-K by probability.
    // Legal mask keeps this cheap — we scan 4352 once.
    let maxLegal = -Infinity;
    for (let i = 0; i < 4352; i++) {
      if (legalMoves[i] === 1 && logitsMove[i] > maxLegal) maxLegal = logitsMove[i];
    }
    let denom = 0;
    for (let i = 0; i < 4352; i++) {
      if (legalMoves[i] === 1) denom += Math.exp(logitsMove[i] - maxLegal);
    }

    const top: Array<{ idx: number; prob: number }> = [];
    for (let i = 0; i < 4352; i++) {
      if (legalMoves[i] !== 1) continue;
      const prob = Math.exp(logitsMove[i] - maxLegal) / denom;
      if (top.length < topK) {
        top.push({ idx: i, prob });
        top.sort((a, b) => b.prob - a.prob);
      } else if (prob > top[top.length - 1].prob) {
        top[top.length - 1] = { idx: i, prob };
        top.sort((a, b) => b.prob - a.prob);
      }
    }

    let playedMoveProb: number | undefined;
    if (playedUci) {
      const lookupUci = wasMirrored ? mirrorMove(playedUci) : playedUci;
      const idx = MOVE_INDEX.get(lookupUci);
      if (idx !== undefined && legalMoves[idx] === 1) {
        playedMoveProb = Math.exp(logitsMove[idx] - maxLegal) / denom;
      }
    }

    let lookupProbs: Record<string, number> | undefined;
    if (lookupUcis && lookupUcis.length > 0) {
      lookupProbs = {};
      for (const uci of lookupUcis) {
        const lu = wasMirrored ? mirrorMove(uci) : uci;
        const idx = MOVE_INDEX.get(lu);
        if (idx !== undefined && legalMoves[idx] === 1) {
          lookupProbs[uci] = Math.exp(logitsMove[idx] - maxLegal) / denom;
        }
      }
    }

    return {
      winProb,
      drawProb,
      lossProb,
      topMoves: top.map((t) => ({
        uci: decodeMove(t.idx, wasMirrored),
        prob: t.prob,
      })),
      playedMoveProb,
      lookupProbs,
    };
  }

  dispose(): void {
    if (!this.worker) return;
    this.worker.terminate();
    this.worker = null;
    this.disposed = true;
    // Settle the cache verdict so a preload() awaiting it doesn't hang past disposal; the ready()
    // rejection in rejectAll() is what actually surfaces the failure to the caller.
    this.cacheVerdictResolve?.('error');
    this.cacheVerdictResolve = null;
    this.rejectAll(new Error('Maia3 disposed'));
  }

  /** True once dispose() has been called — callers can short-circuit. */
  isDisposed(): boolean { return this.disposed; }

  // ── internals ──────────────────────────────────────────────────────────────

  private runInference(
    tokens: Float32Array,
    eloSelf: number,
    eloOppo: number,
  ): Promise<{ logitsMove: Float32Array; logitsValue: Float32Array }> {
    if (!this.worker) return Promise.reject(new Error('worker unavailable'));
    const id = this.nextId++;
    // Clone into a new buffer we can transfer (Float32Array can alias a
    // shared buffer, so we copy to be safe — tensor.ts allocates fresh each
    // call anyway, so this is usually free).
    const buf = tokens.buffer.slice(0);
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker!.postMessage(
        {
          type: 'inference',
          id,
          tokens: buf,
          eloSelf,
          eloOppo,
        },
        [buf],
      );
    });
  }

  private handleMessage = (e: MessageEvent) => {
    const msg = e.data as
      | { type: 'status'; status: MaiaStatus }
      | { type: 'progress'; progress: number }
      | { type: 'error'; message: string; id?: number }
      | {
          type: 'inference-result';
          id: number;
          logitsMove: ArrayBuffer;
          logitsValue: ArrayBuffer;
        };

    switch (msg.type) {
      case 'status':
        this.setStatus(msg.status);
        // First terminal verdict from `init`: 'ready' = served from IndexedDB, 'no-cache' = must fetch.
        // ('loading'/'downloading' are progress states, not verdicts.) Latches once.
        if (msg.status === 'ready' || msg.status === 'no-cache') {
          this.cacheVerdictResolve?.(msg.status);
          this.cacheVerdictResolve = null;
        }
        if (msg.status === 'ready') {
          this.readyResolve?.();
          this.downloadResolve?.();
          this.downloadResolve = null;
          this.downloadReject = null;
          this.downloadPromise = null;
        }
        break;

      case 'progress':
        this.options.onProgress?.(msg.progress);
        break;

      case 'error': {
        const err = new Error(msg.message);
        if (msg.id !== undefined) {
          const p = this.pending.get(msg.id);
          if (p) {
            p.reject(err);
            this.pending.delete(msg.id);
          }
        } else {
          this.setStatus('error');
          this.options.onError?.(msg.message);
          // Unblock any preload() awaiting the cache verdict — otherwise a worker that errors during
          // init leaves it pending forever and preload() never settles (nor rejects).
          this.cacheVerdictResolve?.('error');
          this.cacheVerdictResolve = null;
          this.readyReject?.(err);
          this.downloadReject?.(err);
          this.downloadResolve = null;
          this.downloadReject = null;
          this.downloadPromise = null;
        }
        break;
      }

      case 'inference-result': {
        const p = this.pending.get(msg.id);
        if (p) {
          p.resolve({
            logitsMove: new Float32Array(msg.logitsMove),
            logitsValue: new Float32Array(msg.logitsValue),
          });
          this.pending.delete(msg.id);
        }
        break;
      }
    }
  };

  private setStatus(status: MaiaStatus) {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatus?.(status);
  }

  private rejectAll(err: Error) {
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
    this.readyReject?.(err);
    this.downloadReject?.(err);
  }
}
